import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Scale, LogOut, Building2, CheckCircle, Clock, FileText,
  Loader2, User, PoundSterling, ChevronRight, Briefcase,
  Phone, Mail, Calendar, AlertCircle, Home, Users, ArrowRight, 
  Bell, TrendingUp, BarChart3, Activity
} from "lucide-react";
import { API } from "@/App";

// Stage labels for display
const stageLabels = {
  offer_accepted: "Offer Accepted",
  memo_issued: "Memo Issued",
  aml_verified: "AML Verified",
  proof_of_funds: "Proof of Funds",
  mortgage_offer: "Mortgage Offer",
  contract_pack: "Contract Pack",
  searches_ordered: "Searches Ordered",
  searches_returned: "Searches Returned",
  enquiries_raised: "Enquiries Raised",
  enquiries_resolved: "Enquiries Resolved",
  exchange_ready: "Exchange Ready",
  exchanged: "Exchanged",
  completion: "Completed"
};

// Milestone status options
const STATUS_OPTIONS = [
  { value: "not_started", label: "Not Started", color: "bg-slate-100 text-slate-700" },
  { value: "in_progress", label: "In Progress", color: "bg-blue-100 text-blue-700" },
  { value: "done", label: "Completed", color: "bg-green-100 text-green-700" },
  { value: "blocked", label: "Blocked", color: "bg-red-100 text-red-700" },
];

// Milestone Item Component
const MilestoneItem = ({ milestone, canUpdate, onUpdate }) => {
  const statusConfig = STATUS_OPTIONS.find(s => s.value === milestone.status) || STATUS_OPTIONS[0];

  return (
    <div className={`p-4 rounded-lg border transition-all ${
      milestone.status === "done" ? "bg-green-50 border-green-200" :
      milestone.status === "in_progress" ? "bg-blue-50 border-blue-200" :
      milestone.status === "blocked" ? "bg-red-50 border-red-200" :
      "bg-slate-50 border-slate-200"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {milestone.status === "done" ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : milestone.status === "in_progress" ? (
            <Clock className="w-5 h-5 text-blue-600" />
          ) : milestone.status === "blocked" ? (
            <AlertCircle className="w-5 h-5 text-red-600" />
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-slate-300" />
          )}
          <span className="font-medium">{milestone.name}</span>
        </div>
        {canUpdate ? (
          <Select
            value={milestone.status}
            onValueChange={(value) => onUpdate(milestone, value)}
          >
            <SelectTrigger className="w-32 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
        )}
      </div>
      {milestone.completed_at && (
        <p className="text-xs text-slate-500 mt-2 ml-8">
          Completed: {new Date(milestone.completed_at).toLocaleDateString("en-GB")}
        </p>
      )}
    </div>
  );
};

export const SolicitorPortalDashboard = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetail, setCaseDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("active");

  // Update milestone modal state
  const [updateModal, setUpdateModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    needsAction: 0,
    completed: 0,
    pipelineValue: 0
  });

  const token = localStorage.getItem("solicitor_token");
  const solicitor = JSON.parse(localStorage.getItem("solicitor") || "{}");

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitor-portal/notifications`, { headers });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [token]);

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/solicitor-portal/notifications/${notificationId}/read`, {}, { headers });
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  // Mark all as read
  const markAllRead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/solicitor-portal/notifications/mark-all-read`, {}, { headers });
      fetchNotifications();
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all notifications read:", error);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    setLoadingAnalytics(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitor-portal/analytics`, { headers });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  }, [token]);

  // Format notification time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-GB");
  };

  const fetchCases = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitor-portal/cases`, { headers });
      const casesData = response.data.cases || [];
      setCases(casesData);
      
      // Calculate stats
      const active = casesData.filter(c => c.status === "active" || c.status === "sstc").length;
      const completed = casesData.filter(c => c.status === "completed").length;
      const exchanged = casesData.filter(c => c.status === "exchanged").length;
      const needsAction = casesData.filter(c => {
        // Check if there are milestones this solicitor needs to action
        const milestones = c.milestones || [];
        return milestones.some(m => 
          m.status !== "done" && 
          (m.type === "contract_pack" || m.type === "enquiries_resolved" || 
           m.type === "searches_ordered" || m.type === "searches_returned" ||
           m.type === "enquiries_raised")
        );
      }).length;
      
      const pipelineValue = casesData
        .filter(c => c.status !== "completed")
        .reduce((sum, c) => sum + (c.offer_price || c.asking_price || 0), 0);
      
      setStats({
        total: casesData.length,
        active: active + exchanged,
        needsAction,
        completed,
        pipelineValue
      });
    } catch (error) {
      console.error("Error fetching cases:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
      toast.error("Failed to load cases");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCaseDetail = async (caseId) => {
    setLoadingDetail(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/solicitor-portal/cases/${caseId}`, { headers });
      setCaseDetail(response.data);
    } catch (error) {
      console.error("Error fetching case detail:", error);
      toast.error("Failed to load case details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("solicitor_token");
    localStorage.removeItem("solicitor");
    navigate("/solicitor-portal/login");
  };

  useEffect(() => {
    if (!token) {
      navigate("/solicitor-portal/login");
      return;
    }
    fetchCases();
    fetchNotifications();
  }, [token, navigate, fetchCases, fetchNotifications]);

  // Handle direct case ID in URL
  useEffect(() => {
    if (caseId && cases.length > 0) {
      const caseData = cases.find(c => c.case_id === caseId);
      if (caseData) {
        handleCaseClick(caseData);
      }
    }
  }, [caseId, cases]);

  const handleCaseClick = (caseData) => {
    // Set staff token/user from solicitor data to pass ProtectedRoute check
    // This allows access to the canonical CaseHubPage which requires auth
    const solicitorData = localStorage.getItem("solicitor");
    const solicitorToken = localStorage.getItem("solicitor_token");
    
    if (solicitorData && solicitorToken) {
      // Temporarily set staff auth from solicitor data
      // Parse and add solicitor role if not present
      const solData = JSON.parse(solicitorData);
      solData.role = solData.role || "solicitor";
      localStorage.setItem("token", solicitorToken);
      localStorage.setItem("user", JSON.stringify(solData));
    }
    
    // Navigate to canonical CaseHubPage with solicitor context
    navigate(`/sales/case/${caseData.case_id}?solicitor=true`);
  };

  const handleUpdateMilestone = async () => {
    if (!selectedMilestone || !newStatus) return;

    setUpdating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/solicitor-portal/cases/${selectedCase.case_id}/milestone`,
        {
          milestone_id: selectedMilestone.milestone_id,
          status: newStatus,
          notes: notes
        },
        { headers }
      );

      toast.success("Milestone updated successfully");
      setUpdateModal(false);
      setSelectedMilestone(null);
      setNewStatus("");
      setNotes("");
      
      // Refresh case detail
      fetchCaseDetail(selectedCase.case_id);
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update milestone");
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateModal = (milestone, status) => {
    setSelectedMilestone(milestone);
    setNewStatus(status);
    setUpdateModal(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate progress percentage
  const getProgressPercent = (caseData) => {
    const milestones = caseData.milestones || [];
    if (milestones.length === 0) return 0;
    const done = milestones.filter(m => m.status === "done").length;
    return Math.round((done / milestones.length) * 100);
  };

  // Get status badge
  const getStatusBadge = (caseData) => {
    const stage = caseData.current_stage || caseData.status;
    const label = stageLabels[stage] || stage?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "Active";
    
    let colorClass = "bg-slate-100 text-slate-700";
    if (stage === "completed" || stage === "completion") {
      colorClass = "bg-green-100 text-green-700";
    } else if (stage === "exchanged") {
      colorClass = "bg-blue-100 text-blue-700";
    } else if (stage === "aml_verified" || stage === "proof_of_funds") {
      colorClass = "bg-amber-100 text-amber-700";
    }
    
    return <Badge className={colorClass}>{label}</Badge>;
  };

  // Filter cases by tab
  const getFilteredCases = () => {
    switch (activeTab) {
      case "active":
        return cases.filter(c => c.status !== "completed");
      case "needs_action":
        return cases.filter(c => {
          const milestones = c.milestones || [];
          return milestones.some(m => 
            m.status !== "done" && 
            (m.type === "contract_pack" || m.type === "enquiries_resolved" || 
             m.type === "searches_ordered" || m.type === "searches_returned" ||
             m.type === "enquiries_raised")
          );
        });
      case "all":
      default:
        return cases;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-blue-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {selectedCase && (
                <Button
                  variant="ghost"
                  onClick={() => { setSelectedCase(null); setCaseDetail(null); }}
                  className="text-white hover:bg-blue-800 mr-2"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Scale className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="font-semibold text-lg">
                    {solicitor?.firm_name || "Solicitor Portal"}
                  </h1>
                  <p className="text-sm text-blue-200">
                    {solicitor?.name || solicitor?.email}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Notifications Bell */}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-white hover:bg-blue-800"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                  <div className="bg-blue-900 text-white px-4 py-3 flex items-center justify-between rounded-t-md">
                    <span className="font-medium">Notifications</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-200 hover:text-white text-xs"
                        onClick={markAllRead}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8">
                        <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No notifications yet</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                            !notif.read ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            markNotificationRead(notif.notification_id);
                            if (notif.case_id) {
                              setNotificationsOpen(false);
                              const caseData = cases.find(c => c.case_id === notif.case_id);
                              if (caseData) {
                                handleCaseClick(caseData);
                              }
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            <div className={`w-2 h-2 rounded-full mt-2 ${
                              !notif.read ? "bg-blue-500" : "bg-transparent"
                            }`} />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-800">{notif.message}</p>
                              {notif.property_address && (
                                <p className="text-xs text-slate-500 mt-0.5">{notif.property_address}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-blue-700 text-white hover:bg-blue-800"
                data-testid="solicitor-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!selectedCase ? (
          // Dashboard View
          <div className="space-y-6">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 text-white">
              <h1 className="text-2xl font-bold mb-2">
                My Cases with Goodacres
              </h1>
              <p className="text-blue-100">
                Welcome back, {solicitor?.name || solicitor?.email}
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Cases</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    </div>
                    <Briefcase className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Active</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.active}</p>
                    </div>
                    <Activity className="w-10 h-10 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Needs Action</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.needsAction}</p>
                    </div>
                    <AlertCircle className="w-10 h-10 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Completed</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.completed}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs and Cases List */}
            <Card>
              <CardHeader className="pb-2">
                <Tabs value={activeTab} onValueChange={(val) => {
                  setActiveTab(val);
                  if (val === "analytics" && !analyticsData) {
                    fetchAnalytics();
                  }
                }}>
                  <TabsList>
                    <TabsTrigger value="active" className="gap-2">
                      <Activity className="w-4 h-4" />
                      Active ({cases.filter(c => c.status !== "completed").length})
                    </TabsTrigger>
                    <TabsTrigger value="needs_action" className="gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Needs Action ({stats.needsAction})
                    </TabsTrigger>
                    <TabsTrigger value="all" className="gap-2">
                      <Briefcase className="w-4 h-4" />
                      All Cases ({stats.total})
                    </TabsTrigger>
                    <TabsTrigger value="analytics" className="gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardHeader>
              <CardContent>
                {activeTab === "analytics" ? (
                  // Analytics View
                  <div className="mt-4">
                    {loadingAnalytics ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      </div>
                    ) : analyticsData ? (
                      <div className="space-y-6">
                        {/* Your Firm Stats */}
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-4">
                            {analyticsData.your_firm?.firm_name || "Your Firm"} Performance
                          </h3>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700">{analyticsData.your_firm?.active_files || 0}</p>
                              <p className="text-sm text-slate-600">Active Files</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-amber-700">{analyticsData.your_firm?.exchanges || 0}</p>
                              <p className="text-sm text-slate-600">Exchanges</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-green-700">{analyticsData.your_firm?.completions || 0}</p>
                              <p className="text-sm text-slate-600">Completions</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-purple-700">
                                {analyticsData.your_firm?.avg_days_to_exchange ?? "—"}
                              </p>
                              <p className="text-sm text-slate-600">Avg Days to Exchange</p>
                            </div>
                            <div className="bg-indigo-50 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-indigo-700">
                                {analyticsData.your_firm?.avg_days_to_completion ?? "—"}
                              </p>
                              <p className="text-sm text-slate-600">Avg Days to Completion</p>
                            </div>
                          </div>
                        </div>

                        {/* Peer Comparison */}
                        {analyticsData.peer_average && (
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-4">Peer Comparison</h3>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-slate-50">
                                    <th className="text-left p-3 font-medium">Firm</th>
                                    <th className="text-center p-3 font-medium">Active Files</th>
                                    <th className="text-center p-3 font-medium">Exchanges</th>
                                    <th className="text-center p-3 font-medium">Completions</th>
                                    <th className="text-center p-3 font-medium">Avg Days to Exchange</th>
                                    <th className="text-center p-3 font-medium">Avg Days to Completion</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* Your Firm Row (highlighted) */}
                                  <tr className="border-b bg-blue-50">
                                    <td className="p-3 font-semibold text-blue-700">
                                      {analyticsData.your_firm?.firm_name || "Your Firm"}
                                    </td>
                                    <td className="p-3 text-center font-medium">{analyticsData.your_firm?.active_files || 0}</td>
                                    <td className="p-3 text-center font-medium">{analyticsData.your_firm?.exchanges || 0}</td>
                                    <td className="p-3 text-center font-medium">{analyticsData.your_firm?.completions || 0}</td>
                                    <td className="p-3 text-center font-medium">{analyticsData.your_firm?.avg_days_to_exchange ?? "—"}</td>
                                    <td className="p-3 text-center font-medium">{analyticsData.your_firm?.avg_days_to_completion ?? "—"}</td>
                                  </tr>
                                  {/* Peer Average Row */}
                                  <tr className="border-b bg-slate-100">
                                    <td className="p-3 font-medium text-slate-700">{analyticsData.peer_average?.label}</td>
                                    <td className="p-3 text-center">{analyticsData.peer_average?.active_files}</td>
                                    <td className="p-3 text-center">{analyticsData.peer_average?.exchanges}</td>
                                    <td className="p-3 text-center">{analyticsData.peer_average?.completions}</td>
                                    <td className="p-3 text-center">{analyticsData.peer_average?.avg_days_to_exchange ?? "—"}</td>
                                    <td className="p-3 text-center">{analyticsData.peer_average?.avg_days_to_completion ?? "—"}</td>
                                  </tr>
                                  {/* Anonymised Peer Rows */}
                                  {(analyticsData.peers || []).map((peer) => (
                                    <tr key={peer.label} className="border-b hover:bg-slate-50">
                                      <td className="p-3 text-slate-600">{peer.label}</td>
                                      <td className="p-3 text-center">{peer.active_files}</td>
                                      <td className="p-3 text-center">{peer.exchanges}</td>
                                      <td className="p-3 text-center">{peer.completions}</td>
                                      <td className="p-3 text-center">{peer.avg_days_to_exchange ?? "—"}</td>
                                      <td className="p-3 text-center">{peer.avg_days_to_completion ?? "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No analytics data available</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Cases List View
                  <div className="space-y-3 mt-4">
                  {getFilteredCases().length === 0 ? (
                    <div className="text-center py-12">
                      <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No cases found</p>
                    </div>
                  ) : (
                    getFilteredCases().map((caseItem) => (
                      <div
                        key={caseItem.case_id}
                        onClick={() => handleCaseClick(caseItem)}
                        className="p-4 bg-white border rounded-lg hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                        data-testid={`case-card-${caseItem.case_id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2">
                              <Home className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              <h3 className="font-semibold text-slate-900 truncate">
                                {caseItem.address || caseItem.address_line1 || caseItem.property_address || "Property"}
                              </h3>
                              {getStatusBadge(caseItem)}
                            </div>
                            
                            <div className="ml-8 space-y-2">
                              <div className="flex items-center gap-4 text-sm text-slate-600">
                                <span className="flex items-center gap-1">
                                  <PoundSterling className="w-4 h-4" />
                                  {formatCurrency(caseItem.price || caseItem.offer_price || caseItem.asking_price)}
                                </span>
                                <Badge variant="outline" className="bg-slate-50">
                                  {(caseItem.role || caseItem.solicitor_type || "").includes("buyer") ? "Acting for Buyer" : "Acting for Seller"}
                                </Badge>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                <Progress 
                                  value={caseItem.progress || getProgressPercent(caseItem)} 
                                  className="flex-1 h-2"
                                />
                                <span className="text-sm font-medium text-slate-600 w-12">
                                  {caseItem.progress || getProgressPercent(caseItem)}%
                                </span>
                              </div>
                              
                              {caseItem.seller_name && (
                                <p className="text-sm text-slate-500">
                                  <span className="font-medium">Seller:</span> {caseItem.seller_name}
                                </p>
                              )}
                              {caseItem.buyer_name && (
                                <p className="text-sm text-slate-500">
                                  <span className="font-medium">Buyer:</span> {caseItem.buyer_name}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-2" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          // Case Detail View
          <div className="space-y-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : caseDetail ? (
              <>
                {/* Case Header */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900 mb-2">
                          {caseDetail.case?.address_line1 || caseDetail.case?.property_address}
                        </h2>
                        <div className="flex items-center gap-3 text-slate-600">
                          <span className="flex items-center gap-1">
                            <PoundSterling className="w-4 h-4" />
                            {formatCurrency(caseDetail.case?.offer_price || caseDetail.case?.asking_price)}
                          </span>
                          {getStatusBadge(caseDetail.case || {})}
                          <Badge variant="outline">
                            {selectedCase.solicitor_type === "buyer" ? "Acting for Buyer" : "Acting for Seller"}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Progress</p>
                        <p className="text-3xl font-bold text-blue-600">
                          {getProgressPercent(caseDetail.case || {})}%
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Parties Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Seller
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium">{caseDetail.case?.seller_name || "Not specified"}</p>
                      {caseDetail.case?.seller_solicitor_firm && (
                        <p className="text-sm text-slate-500 mt-1">
                          Solicitor: {caseDetail.case?.seller_solicitor_firm}
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Buyer
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium">{caseDetail.case?.buyer_name || "Not specified"}</p>
                      {caseDetail.case?.buyer_solicitor_firm && (
                        <p className="text-sm text-slate-500 mt-1">
                          Solicitor: {caseDetail.case?.buyer_solicitor_firm}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Milestones */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Sales Progression Milestones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(caseDetail.milestones || []).map((milestone) => (
                        <MilestoneItem
                          key={milestone.milestone_id}
                          milestone={milestone}
                          canUpdate={milestone.can_update}
                          onUpdate={openUpdateModal}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Info */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="w-5 h-5" />
                      Estate Agent Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4" />
                        <span>01234 567890</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        <span>sales@goodacresresidential.co.uk</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Failed to load case details</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Update Milestone Modal */}
      <Dialog open={updateModal} onOpenChange={setUpdateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Milestone</DialogTitle>
            <DialogDescription>
              Update the status of "{selectedMilestone?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this update..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateMilestone} disabled={updating}>
              {updating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitorPortalDashboard;
